import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { apiZodios } from '~api/apiZodios'
import { type RootState } from '~redux/rootReducer'
import { selectWorkspaceOrgs } from '~redux/slices/organizationSlice'
import { lightTokens, typography } from '~tokens/internal'
import { type UserId, type Workflow, type WorkflowStatusEnum } from '~types'
import { loadResourcePermittedOrgIdentitiesWithAdHoc } from '~utils/permissionsUtils'

import Dropdown, { type DropdownOption } from '../Dropdown'
import Icon from '../Icon'

import './WorkflowSelector.scss'

export interface WorkflowSelectorProps {
  selectedWorkflows: Workflow[]
  onChange: (selectedWorkflows: Workflow[]) => void
  placeholder?: string
  disabled?: boolean
  singleSelect?: boolean
  statusFilter?: WorkflowStatusEnum[]
  ownerIds?: string[]
  hasLiveVersion?: boolean | null
  label?: string
}

const WorkflowSelector: React.FC<WorkflowSelectorProps> = ({
  selectedWorkflows,
  onChange,
  placeholder = 'Search workflows',
  disabled = false,
  singleSelect = false,
  statusFilter,
  ownerIds,
  hasLiveVersion = null,
  label,
}) => {
  const userId = useSelector((state: RootState) => state?.auth.userId)
  const workspaceOrgs = useSelector(selectWorkspaceOrgs)

  const ownerIdsKey = ownerIds?.join(',') ?? ''
  const statusFilterKey = statusFilter?.join(',') ?? ''

  const [allWorkflows, setAllWorkflows] = useState<Workflow[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [processPermittedOrgIdentities, setProcessPermittedOrgIdentities] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const loadProcessPolicies = async (): Promise<void> => {
      if (!userId) return
      const processPolicies = await loadResourcePermittedOrgIdentitiesWithAdHoc(
        userId as UserId,
        'processes',
      )
      const workspaceOrgIds = new Set(workspaceOrgs.map((org) => org.id))
      setProcessPermittedOrgIdentities(
        (processPolicies['read'] ?? []).filter((id) => workspaceOrgIds.has(id)),
      )
    }
    loadProcessPolicies()
  }, [userId, workspaceOrgs])

  const listWorkflows = async (): Promise<void> => {
    const organizationIdentityIds =
      ownerIds && ownerIds.length > 0
        ? processPermittedOrgIdentities.filter((id) => ownerIds.includes(id))
        : processPermittedOrgIdentities
    if (!userId || organizationIdentityIds.length === 0) {
      setAllWorkflows([])
      return
    }
    setIsLoading(true)
    try {
      const response = await apiZodios.cases_listWorkflows({
        user_id: userId,
        organization_identity_id_list: organizationIdentityIds,
        status: statusFilter,
        has_live_version: hasLiveVersion,
      })
      setAllWorkflows(response.workflows)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    listWorkflows()
  }, [userId, ownerIdsKey, statusFilterKey, hasLiveVersion, processPermittedOrgIdentities])

  const workflowMap = useMemo(() => {
    const map = new Map<string, Workflow>()
    for (const w of selectedWorkflows) map.set(w.id, w)
    for (const w of allWorkflows) map.set(w.id, w)
    return map
  }, [selectedWorkflows, allWorkflows])

  const filteredWorkflows = useMemo(() => {
    if (!searchQuery) return allWorkflows
    const lowercaseQuery = searchQuery.toLowerCase()
    return allWorkflows.filter((workflow) => workflow.name.toLowerCase().includes(lowercaseQuery))
  }, [allWorkflows, searchQuery])

  const options: DropdownOption[] = useMemo(() => {
    return filteredWorkflows.map((w) => ({
      value: w.id,
      label: w.name,
      avatar:
        w.status === 'archived' ? (
          <Icon name='archive' color={lightTokens.text.secondary} size={typography.fontSize.sm} />
        ) : (
          <Icon name='sensors' color={lightTokens.success.main} size={typography.fontSize.sm} />
        ),
    }))
  }, [filteredWorkflows])

  const handleChange = (value: string | string[]): void => {
    const ids = Array.isArray(value) ? value : [value]
    const newSelection = ids.map((id) => workflowMap.get(id)).filter(Boolean) as Workflow[]
    onChange(newSelection)
  }

  return (
    <Dropdown
      options={options}
      value={singleSelect ? selectedWorkflows[0]?.id : selectedWorkflows.map((w) => w.id)}
      onChange={handleChange}
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      multiple={!singleSelect}
      searchable
      onSearchChange={setSearchQuery}
      searchValue={searchQuery}
      loading={isLoading}
      startAdornment={<Icon name='accountTree' />}
      fullWidth
    />
  )
}

export default WorkflowSelector
